import { UpperCasePipe } from '@angular/common'
import {
	ChangeDetectionStrategy,
	Component,
	input,
	InputSignal,
	OnChanges,
	OnDestroy,
	OnInit,
	output,
	OutputEmitterRef,
	signal,
	SimpleChanges,
	WritableSignal,
	inject,
} from '@angular/core'
import { PositionsInterface } from '@modules/positions-module/interfaces/positions.interface'
import { TranslatePipe } from '@ngx-translate/core'
import { HighlightDirective } from '@shared/directives/higlight.directive'
import { TuiIcon } from '@taiga-ui/core'
import { Subject, takeUntil } from 'rxjs'
import { MarketStatusHelperService } from '@modules/alpaca-module/market-status/services/market-status-helper.service'
import { MarketStatusEnum } from '@modules/alpaca-module/market-status/enums/market-status.enum'
import { PositionSideEnum } from '@modules/positions-module/enums/position-side.enum'
import { TuiSkeleton } from '@taiga-ui/kit'
import { CompanyLogoComponent } from '@shared/components/common/company-logo/company-logo.component'
import { ProfitTriangleComponent } from '@shared/components/trading/common/profit-triangle/profit-triangle.component'
import { UseLocales } from '@modules/lang-module/decorators/use-locales'
import ru from './core/locale/ru.json'
import en from './core/locale/en.json'
import { PricePipe } from '@shared/pipes/price.pipe'
import { QuantityPipe } from '@shared/pipes/quantity.pipe'
import { ProfitPipe } from '@shared/pipes/profit.pipe'
import { PercentPipe } from '@shared/pipes/percent.pipe'
import { LiveTickerInterface } from '@modules/stocks-module/live-ticker/interfaces/live-ticker.interface'
import { AssetExanteInterface } from '@modules/stocks-module/general/interfaces/asset-exante.interface'
import { LiveTickerPricesInterfaces } from '@modules/stocks-module/live-ticker/interfaces/live-ticker-prices.interfaces'
import { StocksService } from '@modules/stocks-module/general/services/stocks.service'
import { SecondaryLiveTickerService } from '@modules/stocks-module/live-ticker/services/secondary-live-ticker.service'

@UseLocales({ ru, en })
@Component({
	selector: 'app-position-info',
	templateUrl: './position-info.component.html',
	styleUrl: './position-info.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		TuiIcon,
		TuiSkeleton,
		TranslatePipe,
		UpperCasePipe,
		HighlightDirective,
		CompanyLogoComponent,
		ProfitTriangleComponent,
		PricePipe,
		QuantityPipe,
		ProfitPipe,
		PercentPipe,
	],
})
export class PositionInfoComponent implements OnChanges, OnDestroy, OnInit {
	protected readonly positionSideEnum: typeof PositionSideEnum = PositionSideEnum
	protected readonly marketStatusEnum: typeof MarketStatusEnum = MarketStatusEnum

	public position: InputSignal<PositionsInterface | null> = input<PositionsInterface | null>(null)

	public closeDropdown: OutputEmitterRef<void> = output<void>()

	private _destroy$: Subject<void> = new Subject<void>()

	private readonly _stocksService: StocksService = inject<StocksService>(StocksService)
	private readonly _secondaryLiveTickerService: SecondaryLiveTickerService =
		inject<SecondaryLiveTickerService>(SecondaryLiveTickerService)
	private readonly _marketStatusHelperService: MarketStatusHelperService =
		inject<MarketStatusHelperService>(MarketStatusHelperService)

	protected liveTicker: LiveTickerInterface | null = null

	protected assetsInfo: WritableSignal<AssetExanteInterface | null> = signal(null)
	protected isLoadingAssetInfo: WritableSignal<boolean> = signal(false)
	protected isLoadingLiveTicker: WritableSignal<boolean> = signal(false)

	get marketStatus(): MarketStatusEnum {
		return this._marketStatusHelperService.marketStatus
	}

	get tickerPrices(): Partial<LiveTickerPricesInterfaces> | null {
		if (!this.liveTicker) return null

		const primary: Partial<LiveTickerPricesInterfaces> | null =
			this.marketStatus === this.marketStatusEnum.marketClose
				? this.liveTicker?.currentPrices
				: this.liveTicker?.marketStatusPrices
		const fallback: Partial<LiveTickerPricesInterfaces> | null =
			this.marketStatus === this.marketStatusEnum.marketClose
				? this.liveTicker?.marketStatusPrices
				: this.liveTicker?.currentPrices

		return {
			price: primary?.price || fallback?.price,
			changeMoney: primary?.changeMoney || fallback?.changeMoney,
			changePercent: primary?.changePercent || fallback?.changePercent,
		}
	}
	private _listenLiveTicker(): void {
		this._secondaryLiveTickerService.liveTicker$
			.pipe(takeUntil(this._destroy$))
			.subscribe((liveTicker: LiveTickerInterface | null): void => {
				this.liveTicker = liveTicker
			})

		this._secondaryLiveTickerService.loadingLiveTicker$
			.pipe(takeUntil(this._destroy$))
			.subscribe((isLoading: boolean): void => {
				this.isLoadingLiveTicker.set(isLoading)
			})
	}

	private _setLiveTicker(symbol: string): void {
		this._secondaryLiveTickerService.setLiveTicker(symbol)
	}

	private _getAssetsInfo(symbol: string): void {
		this.isLoadingAssetInfo.set(true)

		this._stocksService
			.getExanteAsset(symbol)
			.pipe(takeUntil(this._destroy$))
			.subscribe((assets: AssetExanteInterface): void => {
				this.assetsInfo.set(assets)
				this.isLoadingAssetInfo.set(false)
			})
	}

	protected onCloseDropdown(): void {
		this.closeDropdown.emit()
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['position']) {
			const prevSymbol: string | undefined = changes['position']?.previousValue?.symbol
			const currentSymbol: string | undefined = changes['position']?.currentValue?.symbol

			if (currentSymbol && currentSymbol !== prevSymbol) {
				this._getAssetsInfo(currentSymbol)
				this._setLiveTicker(currentSymbol)
			}
		}
	}

	ngOnInit(): void {
		this._listenLiveTicker()
	}

	ngOnDestroy(): void {
		this._destroy$.next()
		this._destroy$.complete()
	}
}
